import { createAsyncThunk } from '@reduxjs/toolkit';
import {
    createConversation,
    deleteMessage,
    fetchConversationMessages,
    getUserConversationState,
    sendMessage,
    getUserPermissions
} from 'src/api/chat';

import {
    CreateConversationResponseSchema,
    MessageActionResponseSchema,
    GetConversationResponseSchema,
    GetUserConversationStateResponseSchema,
    GetUserPermissionsResponseSchema
} from 'src/types/chat';

const SLICE_NAME = 'chat';

const ACTION_TYPES = {
    FETCH_CONVERSATION: `${SLICE_NAME}/fetchConversation`,
    CREATE_CONVERSATION: `${SLICE_NAME}/createNewConversation`,
    FETCH_USER_CONVERSATION_STATE: `${SLICE_NAME}/fetchUserConversationState`,
    SEND_MESSAGE: `${SLICE_NAME}/sendMessage`,
    DELETE_MESSAGE: `${SLICE_NAME}/deleteMessage`,
    GET_USER_PERMISSIONS: `${SLICE_NAME}/getUserPermissions`
};

const fetchConversation = createAsyncThunk<
    GetConversationResponseSchema,
    {
        conversationId: number;
        page: number;
        resultsPerPage: number;
        entityId: string | null;
        query?: string;
    }
>(ACTION_TYPES.FETCH_CONVERSATION, async (options, { rejectWithValue }) => {
    try {
        const response = await fetchConversationMessages(
            options.conversationId,
            options.page,
            options.resultsPerPage,
            options.entityId,
            options.query
        );
        return response as GetConversationResponseSchema;
    } catch (error) {
        return rejectWithValue(error);
    }
});

const createNewConversation = createAsyncThunk<
    CreateConversationResponseSchema,
    {
        name: string;
        entityId: string;
        projectId: number;
        projectName: string;
    }
>(
    ACTION_TYPES.CREATE_CONVERSATION,
    async (
        options: {
            name: string;
            entityId: string;
            projectId: number;
            projectName: string;
        },
        { rejectWithValue }
    ) => {
        try {
            let response = await createConversation(
                options.name,
                options.entityId,
                options.projectId,
                options.projectName
            );
            return response as CreateConversationResponseSchema;
        } catch (error) {
            return rejectWithValue(error);
        }
    }
);

const fetchUserConversationState = createAsyncThunk<
    GetUserConversationStateResponseSchema,
    { entityId: string; resultsPerPage: number }
>(
    ACTION_TYPES.FETCH_USER_CONVERSATION_STATE,
    async (
        options: {
            entityId: string;
            resultsPerPage: number;
        },
        { rejectWithValue }
    ) => {
        try {
            const response = await getUserConversationState(
                options.entityId,
                options.resultsPerPage
            );
            return response;
        } catch (error) {
            return rejectWithValue(error);
        }
    }
);

const fetchUserPermissions = createAsyncThunk<GetUserPermissionsResponseSchema>(
    ACTION_TYPES.GET_USER_PERMISSIONS,
    async (_, { rejectWithValue }) => {
        try {
            const response = await getUserPermissions();
            return response;
        } catch (error) {
            return rejectWithValue(error);
        }
    }
);

const sendNewMessage = createAsyncThunk<
    MessageActionResponseSchema,
    {
        conversationId: number;
        entityId: string;
        tagId: string;
        text: string;
        timestampEpochMs: number;
        replyMessageId?: number;
        attachmentIds?: number[];
    }
>(
    ACTION_TYPES.SEND_MESSAGE,
    async (
        options: {
            conversationId: number;
            entityId: string;
            tagId: string;
            text: string;
            timestampEpochMs: number;
            replyMessageId?: number;
            attachmentIds?: number[];
        },
        { rejectWithValue }
    ) => {
        try {
            const response = await sendMessage(
                options.conversationId,
                options.entityId,
                options.tagId,
                options.text,
                options.timestampEpochMs,
                options.replyMessageId,
                options.attachmentIds
            );
            return response;
        } catch (error) {
            return rejectWithValue(error);
        }
    }
);

const deleteExistingMessage = createAsyncThunk<
    MessageActionResponseSchema,
    {
        conversationId: number;
        messageId: number;
    }
>(
    ACTION_TYPES.DELETE_MESSAGE,
    async (
        options: {
            conversationId: number;
            messageId: number;
        },
        { rejectWithValue }
    ) => {
        try {
            const response = await deleteMessage(
                options.conversationId,
                options.messageId
            );
            return response;
        } catch (error) {
            return rejectWithValue(error);
        }
    }
);

export {
    SLICE_NAME,
    ACTION_TYPES,
    createNewConversation,
    fetchConversation,
    fetchUserConversationState,
    sendNewMessage,
    deleteExistingMessage,
    fetchUserPermissions
};
